import { _MiscMessages, ConfigSystemParams, DashboardProfit } from 'src/app/core/consts';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, map, merge, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { DashboardService } from 'src/app/core/services/utelize/general/dashboard-service';
import { DashboardStatistic, DashboardStatistic_EmptyObject } from 'src/app/core/interfaces/general/dashboard-statistic';
import { DashboardTotal_Previous_Current_PercentDTO_EmptyObject } from 'src/app/core/models/system/dashboard/dashboard-total-previous-current-percent.dto';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-widget-row-total-costs',
  templateUrl: './widget-row-total-costs.component.html',
  styleUrls: ['./widget-row-total-costs.component.scss'],
  providers: [TippyToolTipService]
})

export class WidgetRowTotalCostsComponent implements AfterViewInit, OnDestroy, OnInit {
  
  @Input() allowContainerClose: boolean | undefined;
  @Input() containerTitle     : string  | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() showTotalUsage     : boolean | undefined=true;
  @Input() useContainer       : boolean | undefined;
  @Input() showTitleHintText  : boolean | undefined;
    
  @Output() containerClosed: EventEmitter<void>=new EventEmitter<void>();
    
  get pollingWidgets  () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.WidgetRow_Polling_TotalCosts,   false); }
  get tbDisplayEnabled() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.DashboardUsageDisplayTbEnabled, true ); }
    
  get maxWidgetsPerRow() { return this.configSystemService.getValueAsInteger(ConfigSystemParams.WidgetRow_MaxWidgetsPerRow_TotalCosts, 4); }

  private filterCompanyID                   : number | undefined;
  private filterDate                        : OurDate | undefined=populateOurDate(this.exceptionsService, Date.now());
  private statTitleTotalCostsLicence        : string="Licence costs";
  private statTitleTotalCostsUsage          : string="Usage costs";
  private statTitleTotalCostsUsageAndLicence: string="Usage & licence costs";
  private statTitleTotalUsage               : string="Usage";
  
  private destroy$  =new Subject<void>();
  private selection$=new Subject<{ companyId: number; date: { year: number; month: number; day: number } }>();

  private readonly USAGE_TB_THRESHOLD_GB: number=1024;
  
  public expandContainer : boolean=true;
  public statData!       : Array<DashboardStatistic>;
  public visibleContainer: boolean=true;

  constructor(
    private changeDetectorRef  : ChangeDetectorRef,
    private configSystemService: ConfigSystemService,
    private dashboardService   : DashboardService,
    private exceptionsService  : ExceptionsService,
    private tippyToolTipService: TippyToolTipService
  ) {

  }

  public ngOnInit(): void {
    try {
      this.initSelectionStream();

      if (!this.containerTitle || this.containerTitle.trim()=="")
        this.containerTitle=_MiscMessages.TitleNotSet;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngAfterViewInit(): void {
    try {
      setTimeout(() => {
        this.displayData();
      });
      
      this.tippyToolTipService.setupBootstrapTooltips('[data-bs-toggle="tooltip"]', 'left');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngOnDestroy(): void {
    try {
      this.destroy$.next();
      this.destroy$.complete();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private displayData() {
    try {
      this.statData=[];
                   
      let statTotalCostsLicence: DashboardStatistic={
        decimalPlaces : 2,
        icon          : "bx-pound",
        iconColour    : "success",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        prefix        : "£",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        subTitle      : "During month",
        title         : this.statTitleTotalCostsLicence,
        value         : undefined,
      }

      let statTotalCostsUsageAndLicence: DashboardStatistic={
        decimalPlaces : 2,
        icon          : "bx-pound",
        iconColour    : "success",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        prefix        : "£",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        subTitle      : "During month",
        title         : this.statTitleTotalCostsUsageAndLicence,
        value         : undefined,
      }

      let statTotalCostsUsage: DashboardStatistic={
        decimalPlaces : 2,
        icon          : "bx-pound",
        iconColour    : "success",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        prefix        : "£",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        subTitle      : "During month",
        title         : this.statTitleTotalCostsUsage,
        value         : undefined,
      }
            
      let statTotalUsage: DashboardStatistic=DashboardStatistic_EmptyObject();
      
      if (this.showTotalUsage) {
        statTotalUsage={
          decimalPlaces : 2,
          icon          : "bx-line-chart",
          iconColour    : "warning",
          link          : undefined,
          linkText      : undefined,
          loading       : true,
          percentage    : <string><unknown>"",
          profit        : "",
          profitUpIsGood: true,
          showProfit    : true,
          showSpinner   : false,
          subTitle      : "During month",
          title         : this.statTitleTotalUsage,
          unit          : "GB",
          value         : undefined,
        }
        
        this.statData.push(statTotalUsage);
      }

      this.statData.push(statTotalCostsUsage);
      this.statData.push(statTotalCostsLicence);
      this.statData.push(statTotalCostsUsageAndLicence);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
       return (undefined);
    }
  }
  
  private initSelectionStream(): void {
    try {
      this.selection$
      .pipe(
        debounceTime(150),
        distinctUntilChanged((a, b) =>
          a.companyId ===b.companyId  &&
          a.date.year ===b.date.year  &&
          a.date.month===b.date.month &&
          a.date.day  ===b.date.day
        ),
        switchMap(selected => {
          const companyId=selected.companyId;
          const year     =selected.date.year;
          const month    =selected.date.month;
          const day      =selected.date.day;

          this.resetTilesToLoading();

          const safeTile=<T>(observable: Observable<T>, applyFn: (data: T) => void) =>
            observable.pipe(
              catchError(err => {
                this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(err)));
                return EMPTY;
              }),
              map(data => { applyFn(data); })
            );

          const totalCostsLicenceSource=this.pollingWidgets
                                        ? this.dashboardService.getTotalCostsLicenceUpdates(companyId, year, month, day)
                                        : this.dashboardService.getTotalCostsLicence       (companyId, year, month, day);

          const totalCostsUsageSource=this.pollingWidgets
                                      ? this.dashboardService.getTotalCostsUsageUpdates(companyId, year, month, day)
                                      : this.dashboardService.getTotalCostsUsage       (companyId, year, month, day);

          const totalCostsUsageAndLicenceSource=this.pollingWidgets
                                                ? this.dashboardService.getTotalCostsUsageAndLicenceUpdates(companyId, year, month, day)
                                                : this.dashboardService.getTotalCostsUsageAndLicence       (companyId, year, month, day);

          const totalUsageSource=this.showTotalUsage
                                 ? (
                                    this.pollingWidgets
                                    ? this.dashboardService.getTotalUsageUpdates(companyId, year, month, day)
                                    : this.dashboardService.getTotalUsage       (companyId, year, month, day)
                                   )
                                 : of(DashboardTotal_Previous_Current_PercentDTO_EmptyObject());

          return merge(
            safeTile(totalCostsLicenceSource,         d => this.applyTotalCostsLicence        (d)),
            safeTile(totalCostsUsageSource,           d => this.applyTotalCostsUsage          (d)),
            safeTile(totalCostsUsageAndLicenceSource, d => this.applyTotalCostsUsageAndLicence(d)),
            safeTile(totalUsageSource,                d => { if (this.showTotalUsage) this.applyTotalUsage(d); })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private resetTilesToLoading(): void {
    try {
      if (!this.statData)
        return;

      this.statData.forEach((stat: DashboardStatistic) => {
        stat.loading=true;
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
    
  public emitSelection(filterCompanyID: number | undefined, filterDate: OurDate | undefined): void {
    try {
      this.filterCompanyID=filterCompanyID;
      this.filterDate     =filterDate;

      const companyId=this.filterCompanyID ?? -1;
      const date     ={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1),
      };
      
      this.selection$.next({ companyId, date });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalCostsLicence(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleTotalCostsLicence) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal;

          stat.iconColour=Number((Number(data.currentTotal) || 0).toFixed(stat.decimalPlaces ?? 2))>0 ? "success" : "danger";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalCostsUsage(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleTotalCostsUsage) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal;

          stat.iconColour=Number((Number(data.currentTotal) || 0).toFixed(stat.decimalPlaces ?? 2))>0 ? "success" : "danger";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalCostsUsageAndLicence(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleTotalCostsUsageAndLicence) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal;

          stat.iconColour=Number((Number(data.currentTotal) || 0).toFixed(stat.decimalPlaces ?? 2))>0 ? "success" : "danger";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalUsage(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.showTotalUsage)
        return;
      
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleTotalUsage) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public trendPercent(stat: DashboardStatistic): number | null {
    try {
      if (!stat?.showProfit || stat?.percentage==null || stat.percentage=="")
        return null;

      const percent=Number(stat.percentage);

      return Number.isFinite(percent) ? percent : null;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return null;
    }
  }

  private showUsageAsTb(stat: DashboardStatistic): boolean {
    try {
      if (stat?.unit!=='GB' || typeof stat.value!=='number' || !this.tbDisplayEnabled)
        return false;

      return stat.value>=this.USAGE_TB_THRESHOLD_GB;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public usageSuffix(stat: DashboardStatistic): string | undefined {
    try {
      return this.showUsageAsTb(stat) ? 'TB' : stat?.unit;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return stat?.unit;
    }
  }

  public usageValue(stat: DashboardStatistic): string | number | null {
    try {
      if (this.showUsageAsTb(stat))
        return <number>stat.value/this.USAGE_TB_THRESHOLD_GB;

      return stat?.value ?? null;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return stat?.value ?? null;
    }
  }

  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public collapseContainer(): void {
    try {
      this.expandContainer=false;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public restoreContainer(): void {
    try {
      this.visibleContainer=true;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
